import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../auth/firebase';
import { usePractice } from '../../api/queries';
import { apiCall } from '../../api/client';
import { PhoneInput } from '../../components/common/PhoneInput';
import { TimePicker } from '../../components/common/TimePicker';
import { to12h, to24h } from '../../utils/time';
import { resizeImage } from '../../utils/resizeImage';
import { SCHED_DAYS, WEEKDAYS } from '../../constants';
import { SmsConsentDisclosure } from '../../components/common/SmsConsentDisclosure';
import { SMS_CONSENT_SCRIPT_PROVIDER } from '../../constants';
import type { SchedulingWindow, Subscription } from '../../types';

export function SettingsPage() {
  const location = useLocation();
  const { data: practiceData, refetch } = usePractice();
  const [activeTab, setActiveTab] = useState<'provider' | 'practice'>('provider');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');

  // Provider state
  const [provName, setProvName] = useState('');
  const [provPhone, setProvPhone] = useState('');
  const [provSmsConsent, setProvSmsConsent] = useState(false);
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [headshotUrl, setHeadshotUrl] = useState('');

  // Practice state
  const [practiceName, setPracticeName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [brandColor, setBrandColor] = useState('#2563EB');
  const [defaultDuration, setDefaultDuration] = useState('30');
  const [practiceTimezone, setPracticeTimezone] = useState('America/New_York');
  const [schedWindows, setSchedWindows] = useState<Record<string, { enabled: boolean; startHour: number; startMin: string; startAmpm: string; endHour: number; endMin: string; endAmpm: string }>>({});

  // Team state
  const [team, setTeam] = useState<Array<{ uid: string; name: string; email: string; role: string }>>([]);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');

  // Init from practice data
  useEffect(() => {
    if (!practiceData) return;
    const p = practiceData.provider;
    const pr = practiceData.practice;
    const b = pr.branding || {};
    const s = pr.settings || {};

    setProvName(p.name || '');
    setProvPhone(p.phone || '');
    setProvSmsConsent(!!p.smsConsent);
    if (p.headshotUrl) setHeadshotUrl(p.headshotUrl);

    setPracticeName(b.practiceName || pr.name || '');
    if (b.primaryColor) setBrandColor(b.primaryColor);
    if (b.logoUrl) setLogoUrl(b.logoUrl);
    if (s.defaultVisitDuration) setDefaultDuration(String(s.defaultVisitDuration));
    if (s.timezone) setPracticeTimezone(s.timezone);

    const windows: Record<string, { enabled: boolean; startHour: number; startMin: string; startAmpm: string; endHour: number; endMin: string; endAmpm: string }> = {};
    for (const day of SCHED_DAYS) {
      const w = s.schedulingWindows?.[day.key] || { enabled: WEEKDAYS.includes(day.key), start: '09:00', end: '17:00' };
      const st = to12h(w.start);
      const en = to12h(w.end);
      windows[day.key] = { enabled: w.enabled, startHour: st.hour12, startMin: st.minute, startAmpm: st.ampm, endHour: en.hour12, endMin: en.minute, endAmpm: en.ampm };
    }
    setSchedWindows(windows);

    // Load team
    apiCall<{ providers: Array<{ uid: string; name: string; email: string; role: string }> }>('/providers/list').then((data) => {
      setTeam(data.providers || []);
    }).catch(() => {});
  }, [practiceData]);

  // Tab from hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'practice') setActiveTab('practice');
    else setActiveTab('provider');
  }, [location.hash]);

  const updateSchedDay = (dayKey: string, field: string, value: unknown) => {
    setSchedWindows((prev) => ({ ...prev, [dayKey]: { ...prev[dayKey], [field]: value } }));
  };

  const saveProvider = async () => {
    setError('');
    if (!provName.trim()) { setError('Provider name is required.'); return; }
    if (!provPhone.trim()) { setError('Cell phone number is required.'); return; }
    if (!provSmsConsent) { setError('SMS consent is required.'); return; }
    setSaving(true);
    setSaveStatus('Saving...');
    try {
      const body: Record<string, unknown> = { name: provName.trim(), phone: provPhone.trim(), smsConsent: provSmsConsent };
      if (headshotFile && practiceData) {
        const resized = await resizeImage(headshotFile);
        const ext = resized.type === 'image/png' ? 'png' : 'jpg';
        const storageRef = ref(storage, `practices/${practiceData.practiceId}/headshots/${practiceData.provider.uid}.${ext}`);
        await uploadBytes(storageRef, resized, { contentType: resized.type });
        body.headshotUrl = await getDownloadURL(storageRef);
        setHeadshotUrl(body.headshotUrl as string);
        setHeadshotFile(null);
      } else if (headshotUrl) {
        body.headshotUrl = headshotUrl;
      } else {
        body.headshotUrl = null;
      }
      await apiCall('/practices/profile', { method: 'PUT', body });
      setSaveStatus('Saved');
      setTimeout(() => setSaveStatus(''), 3000);
      refetch();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSaveStatus('');
    } finally {
      setSaving(false);
    }
  };

  const savePractice = async () => {
    setError('');
    if (!practiceName.trim()) { setError('Practice name is required.'); return; }
    setSaving(true);
    setSaveStatus('Saving...');
    try {
      await apiCall('/practices/profile', { method: 'PUT', body: { practiceName: practiceName.trim() } });
      const brandingUpdates: Record<string, unknown> = { primaryColor: brandColor };
      if (logoFile && practiceData) {
        const resized = await resizeImage(logoFile);
        const ext = resized.type === 'image/png' ? 'png' : 'jpg';
        const storageRef = ref(storage, `practices/${practiceData.practiceId}/logo/logo.${ext}`);
        await uploadBytes(storageRef, resized, { contentType: resized.type });
        brandingUpdates.logoUrl = await getDownloadURL(storageRef);
        setLogoUrl(brandingUpdates.logoUrl as string);
        setLogoFile(null);
      } else if (logoUrl) {
        brandingUpdates.logoUrl = logoUrl;
      }
      await apiCall('/practices/branding', { method: 'PUT', body: brandingUpdates });

      const windows: Record<string, SchedulingWindow> = {};
      for (const day of SCHED_DAYS) {
        const w = schedWindows[day.key];
        if (!w) continue;
        const start = to24h(w.startHour, w.startMin, w.startAmpm);
        const end = to24h(w.endHour, w.endMin, w.endAmpm);
        if (w.enabled && start >= end) { setError(`${day.label}: end time must be after start time.`); setSaving(false); setSaveStatus(''); return; }
        windows[day.key] = { enabled: w.enabled, start, end };
      }
      await apiCall('/practices/settings', { method: 'PUT', body: { defaultVisitDuration: parseInt(defaultDuration) || 30, timezone: practiceTimezone, schedulingWindows: windows } });
      setSaveStatus('Saved');
      setTimeout(() => setSaveStatus(''), 3000);
      refetch();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSaveStatus('');
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) return;
    try {
      await apiCall('/providers/invite', { method: 'POST', body: { name: inviteName.trim(), email: inviteEmail.trim() } });
      setInviteName(''); setInviteEmail('');
      const data = await apiCall<{ providers: Array<{ uid: string; name: string; email: string; role: string }> }>('/providers/list');
      setTeam(data.providers || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invite failed');
    }
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <Link to="/dashboard" className="text-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--primary-color)', textDecoration: 'none' }}>&larr; Back to Dashboard</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span className="text-sm text-muted">{saveStatus}</span>
          <button className="btn btn--primary btn--sm" onClick={activeTab === 'practice' ? savePractice : saveProvider} disabled={saving}>
            {saving ? <><span style={{ visibility: 'hidden' }}>Save Changes</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Save Changes'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-color)', marginBottom: '1.5rem' }}>
        <button className={`settings-tab${activeTab === 'provider' ? ' settings-tab--active' : ''}`} style={{ padding: '0.75rem 1.25rem', fontSize: '0.9375rem', fontWeight: 500, color: activeTab === 'provider' ? 'var(--primary-color)' : 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', borderBottom: activeTab === 'provider' ? '2px solid var(--primary-color)' : '2px solid transparent', marginBottom: '-2px' }} onClick={() => { window.location.hash = 'provider'; }}>Provider Settings</button>
        <button className={`settings-tab${activeTab === 'practice' ? ' settings-tab--active' : ''}`} style={{ padding: '0.75rem 1.25rem', fontSize: '0.9375rem', fontWeight: 500, color: activeTab === 'practice' ? 'var(--primary-color)' : 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', borderBottom: activeTab === 'practice' ? '2px solid var(--primary-color)' : '2px solid transparent', marginBottom: '-2px' }} onClick={() => { window.location.hash = 'practice'; }}>Practice Settings</button>
      </div>

      {error && <div className="alert alert--error">{error}</div>}

      {activeTab === 'provider' ? (
        <div className="card">
          <h2 className="mb-2">Provider Settings</h2>
          <div className="form-group">
            <label htmlFor="prov-providerNameField">Provider Name</label>
            <input type="text" id="prov-providerNameField" maxLength={200} value={provName} onChange={(e) => setProvName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" disabled value={practiceData?.provider?.email || ''} style={{ background: '#f3f4f6', color: 'var(--text-muted)', cursor: 'not-allowed' }} />
          </div>
          <div className="form-group">
            <label>Cell Phone <span style={{ color: 'var(--error-color)' }}>*</span></label>
            <PhoneInput value={provPhone} onChange={setProvPhone} />
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', fontWeight: 'normal', marginBottom: '1rem' }}>
            <input type="checkbox" checked={provSmsConsent} onChange={(e) => setProvSmsConsent(e.target.checked)} style={{ marginTop: '0.2rem', flexShrink: 0, width: '16px', height: '16px' }} />
            <span className="text-sm text-muted">Receive SMS visit reminders and notifications. <span style={{ color: 'var(--error-color)' }}>*</span> <SmsConsentDisclosure intro="Full SMS consent disclosure:" script={SMS_CONSENT_SCRIPT_PROVIDER} /></span>
          </label>
          <div className="form-group">
            <label>Headshot</label>
            <div
              style={{ border: '2px dashed var(--border-color)', borderRadius: 'var(--radius)', padding: '1.5rem', textAlign: 'center', cursor: 'pointer' }}
              onClick={() => document.getElementById('prov-headshotFileInput')?.click()}
            >
              {headshotUrl || headshotFile ? (
                <img src={headshotFile ? URL.createObjectURL(headshotFile) : headshotUrl} style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto', display: 'block' }} alt="Headshot" />
              ) : (
                <>
                  <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--border-color)', margin: '0 auto 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  </div>
                  <p className="text-muted text-sm">Click to upload a photo</p>
                </>
              )}
              <input type="file" id="prov-headshotFileInput" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) setHeadshotFile(e.target.files[0]); }} />
            </div>
            {(headshotUrl || headshotFile) && (
              <button className="btn btn--outline btn--sm mt-1" onClick={() => { setHeadshotFile(null); setHeadshotUrl(''); }}>Remove</button>
            )}
          </div>
          <button className="btn btn--primary btn--full" onClick={saveProvider} disabled={saving}>
            {saving ? <><span style={{ visibility: 'hidden' }}>Save Changes</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Save Changes'}
          </button>
        </div>
      ) : (
        <>
          <div className="card">
            <h2 className="mb-2">Practice Settings</h2>
            <div className="form-group">
              <label>Practice Name <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <input type="text" maxLength={200} value={practiceName} onChange={(e) => setPracticeName(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Practice Logo</label>
              <div
                style={{ border: '2px dashed var(--border-color)', borderRadius: 'var(--radius)', padding: '2rem', textAlign: 'center', cursor: 'pointer' }}
                onClick={() => document.getElementById('logoFileInput')?.click()}
              >
                {logoUrl || logoFile ? (
                  <img src={logoFile ? URL.createObjectURL(logoFile) : logoUrl} style={{ maxWidth: '200px', maxHeight: '100px', margin: '0 auto', display: 'block' }} alt="Logo" />
                ) : (
                  <p className="text-muted text-sm">Drag & drop your logo here, or click to browse</p>
                )}
                <input type="file" id="logoFileInput" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) setLogoFile(e.target.files[0]); }} />
              </div>
              {(logoUrl || logoFile) && <button className="btn btn--outline btn--sm mt-1" onClick={() => { setLogoFile(null); setLogoUrl(''); }}>Remove</button>}
            </div>
            <div className="form-group">
              <label>Brand Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} style={{ width: '48px', height: '36px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius)', padding: '2px', cursor: 'pointer' }} />
                <span className="text-sm text-muted">{brandColor.toUpperCase()}</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 className="mb-2">Scheduling</h2>
            <div className="scheduling-grid">
              {SCHED_DAYS.map((day) => {
                const w = schedWindows[day.key];
                if (!w) return null;
                return (
                  <div key={day.key} className={`scheduling-day${!w.enabled ? ' scheduling-day--disabled' : ''}`}>
                    <label className="scheduling-day__toggle">
                      <input type="checkbox" checked={w.enabled} onChange={(e) => updateSchedDay(day.key, 'enabled', e.target.checked)} />
                      <span className="scheduling-day__label">{day.label}</span>
                    </label>
                    <div className="scheduling-day__times">
                      <TimePicker hour={w.startHour} minute={w.startMin} ampm={w.startAmpm} onHourChange={(h) => updateSchedDay(day.key, 'startHour', h)} onMinuteChange={(m) => updateSchedDay(day.key, 'startMin', m)} onAmpmChange={(a) => updateSchedDay(day.key, 'startAmpm', a)} disabled={!w.enabled} />
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>–</span>
                      <TimePicker hour={w.endHour} minute={w.endMin} ampm={w.endAmpm} onHourChange={(h) => updateSchedDay(day.key, 'endHour', h)} onMinuteChange={(m) => updateSchedDay(day.key, 'endMin', m)} onAmpmChange={(a) => updateSchedDay(day.key, 'endAmpm', a)} disabled={!w.enabled} />
                    </div>
                  </div>
                );
              })}
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '1.25rem 0' }} />
            <div className="form-group">
              <label>Visit Duration</label>
              <select value={defaultDuration} onChange={(e) => setDefaultDuration(e.target.value)}>
                <option value="15">15 minutes</option><option value="30">30 minutes</option><option value="45">45 minutes</option><option value="60">60 minutes</option>
              </select>
            </div>
            <div className="form-group">
              <label>Timezone</label>
              <select value={practiceTimezone} onChange={(e) => setPracticeTimezone(e.target.value)}>
                <option value="America/New_York">Eastern (ET)</option><option value="America/Chicago">Central (CT)</option><option value="America/Denver">Mountain (MT)</option><option value="America/Los_Angeles">Pacific (PT)</option><option value="America/Anchorage">Alaska (AKT)</option><option value="Pacific/Honolulu">Hawaii (HT)</option>
              </select>
            </div>
          </div>

          {/* Scheduling Links & Widget */}
          {practiceData?.practiceId && (
            <SchedulingLinks practiceId={practiceData.practiceId} team={team} />
          )}

          <div className="card">
            <h2 className="mb-2">Team</h2>
            <div className="invite-row">
              <input type="text" placeholder="Provider name" maxLength={200} value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
              <input type="email" placeholder="Email address" style={{ minWidth: '180px' }} value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
              <button className="btn btn--primary btn--sm" onClick={handleInvite}>Invite</button>
            </div>
            <div className="team-list">
              {team.map((t) => (
                <div key={t.uid} className="team-row">
                  <div className="team-row__info">
                    <div className="team-row__name">{t.name}</div>
                    <div className="team-row__email">{t.email}</div>
                  </div>
                  <span className="badge badge--neutral">{t.role}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Billing */}
          <BillingCard subscription={practiceData?.practice?.subscription} />

          <button className="btn btn--primary btn--full" onClick={savePractice} disabled={saving}>
            {saving ? <><span style={{ visibility: 'hidden' }}>Save Changes</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Save Changes'}
          </button>
        </>
      )}
    </div>
  );
}

function SchedulingLinks({ practiceId, team }: { practiceId: string; team: Array<{ uid: string; name: string; email: string; role: string }> }) {
  const baseUrl = window.location.origin;
  const activeProviders = team.filter((p) => p.role !== 'invited');

  const items: Array<{ label: string; link: string; embed: string }> = [];

  const practiceLink = `${baseUrl}/book/${practiceId}`;
  const practiceEmbed = `<iframe src="${baseUrl}/widget.html?practice=${practiceId}" width="320" height="200" frameborder="0" style="border:none; border-radius:12px;"></iframe>`;
  items.push({ label: 'Practice Links', link: practiceLink, embed: practiceEmbed });

  for (const p of activeProviders) {
    const link = `${baseUrl}/book/${practiceId}?provider=${p.uid}`;
    const embed = `<iframe src="${baseUrl}/widget.html?practice=${practiceId}&provider=${p.uid}" width="320" height="200" frameborder="0" style="border:none; border-radius:12px;"></iframe>`;
    items.push({ label: p.name, link, embed });
  }

  const handleCopy = (value: string, btn: HTMLButtonElement) => {
    navigator.clipboard.writeText(value).then(() => {
      const orig = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = orig; }, 1500);
    });
  };

  return (
    <div className="card">
      <h2 className="mb-2">Patient Self Scheduling</h2>

      <p className="text-sm text-muted mb-2">Share scheduling links with patients or embed a booking widget on your website.</p>
      {items.map((item, i) => (
        <div key={i}>
          {i === 1 && items.length > 1 && (
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginTop: '1rem', marginBottom: '0.25rem' }}>Provider Specific Links</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)' }}>
            <span className="text-sm" style={{ fontWeight: 500 }}>{item.label}</span>
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              <button className="btn btn--outline btn--sm" onClick={(e) => handleCopy(item.link, e.currentTarget)}>Copy Link</button>
              <button className="btn btn--outline btn--sm" onClick={(e) => handleCopy(item.embed, e.currentTarget)}>Copy Widget</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BillingCard({ subscription }: { subscription: Subscription | undefined }) {
  const status = subscription?.status || 'none';
  const [loading, setLoading] = useState(false);

  const handleManage = async () => {
    setLoading(true);
    try {
      const data = await apiCall<{ url: string }>('/billing/portal', { method: 'POST' });
      window.location.href = data.url;
    } catch {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const data = await apiCall<{ url: string }>('/billing/checkout', { method: 'POST' });
      window.location.href = data.url;
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <h2 className="mb-2">Billing</h2>
      {status === 'trialing' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className="badge badge--info">Trial</span>
            {subscription?.trialEnd && <span className="text-sm text-muted">Ends {new Date(subscription.trialEnd as number).toLocaleDateString()}</span>}
          </div>
          <p className="text-sm text-muted mb-2">Your card will be charged $29/month (+ $20/month per additional provider) after the trial ends.</p>
          <button className="btn btn--outline btn--sm" onClick={handleManage} disabled={loading}>
            {loading ? <><span style={{ visibility: 'hidden' }}>Manage Billing</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Manage Billing'}
          </button>
        </>
      )}
      {status === 'active' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className="badge badge--success">Active</span>
            <span className="text-sm" style={{ fontWeight: 600 }}>$29/month + $20/seat</span>
          </div>
          {subscription?.cancelAtPeriodEnd
            ? <p className="text-sm mb-2" style={{ color: 'var(--error-color)' }}>Cancels at end of period ({subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd as number).toLocaleDateString() : '—'})</p>
            : <p className="text-sm text-muted mb-2">Next billing date: {subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd as number).toLocaleDateString() : '—'}</p>
          }
          <button className="btn btn--outline btn--sm" onClick={handleManage} disabled={loading}>
            {loading ? <><span style={{ visibility: 'hidden' }}>Manage Billing</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Manage Billing'}
          </button>
        </>
      )}
      {status === 'past_due' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className="badge badge--error">Past Due</span>
          </div>
          <p className="text-sm mb-2" style={{ color: 'var(--error-color)' }}>Payment failed. Please update your payment method.</p>
          <button className="btn btn--primary btn--sm" onClick={handleManage} disabled={loading}>
            {loading ? <><span style={{ visibility: 'hidden' }}>Update Payment Method</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Update Payment Method'}
          </button>
        </>
      )}
      {status === 'canceled' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className="badge badge--neutral">Canceled</span>
          </div>
          <p className="text-sm text-muted mb-2">Your subscription has been canceled. Subscribe to start visits again.</p>
          <button className="btn btn--primary btn--sm" onClick={handleSubscribe} disabled={loading}>
            {loading ? <><span style={{ visibility: 'hidden' }}>Subscribe — $29/month</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Subscribe — $29/month'}
          </button>
        </>
      )}
      {status === 'none' && (
        <>
          <p className="text-sm text-muted mb-2">No active subscription. Subscribe to start creating visits.</p>
          <p className="text-sm mb-2"><strong>$29/month</strong> (+ $20/month per additional provider) with a 7-day free trial</p>
          <button className="btn btn--primary btn--sm" onClick={handleSubscribe} disabled={loading}>
            {loading ? <><span style={{ visibility: 'hidden' }}>Start Free Trial</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Start Free Trial'}
          </button>
        </>
      )}
    </div>
  );
}
